import { useEffect, useMemo, useState } from "react";
import { CATEGORY_LABELS, MARKET_LABELS, type Signal } from "@shared/schema/signal";
import type { SignalsResponse } from "@shared/schema/api";
import { ViewSwitcher } from "./components/ViewSwitcher";
import { SignalFilters } from "./components/SignalFilters";
import { MorningBrief } from "./views/MorningBrief";
import { SignalFeed } from "./views/SignalFeed";
import { SignalMatrix } from "./views/SignalMatrix";
import type { CategoryFilter, MarketFilter, View } from "./lib";

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  const value = localStorage.getItem(key) as T | null;
  return value && allowed.includes(value) ? value : fallback;
}

export function App() {
  const [view, setView] = useState<View>(() => readStored("lr-view", ["brief", "feed", "matrix"], "brief"));
  const [market, setMarket] = useState<MarketFilter>(() => readStored("lr-market", ["ALL", ...Object.keys(MARKET_LABELS)] as MarketFilter[], "ALL"));
  const [category, setCategory] = useState<CategoryFilter>(() => readStored("lr-category", ["ALL", ...Object.keys(CATEGORY_LABELS)] as CategoryFilter[], "ALL"));
  const [signals, setSignals] = useState<Signal[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string>("");
  const [productionData, setProductionData] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void fetch("/api/signals?limit=500", { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json<SignalsResponse>();
      })
      .then((data) => {
        setSignals(data.signals);
        setGeneratedAt(data.meta.generatedAt);
        setProductionData(data.meta.productionData);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (!(caught instanceof DOMException && caught.name === "AbortError")) setError("最新信号暂时无法读取，请稍后重试。");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  useEffect(() => { localStorage.setItem("lr-view", view); }, [view]);
  useEffect(() => { localStorage.setItem("lr-market", market); }, [market]);
  useEffect(() => { localStorage.setItem("lr-category", category); }, [category]);

  const filtered = useMemo(() => signals
    .filter((signal) => market === "ALL" || signal.market === market)
    .filter((signal) => category === "ALL" || signal.category === category), [signals, market, category]);
  const updated = generatedAt ? new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(generatedAt)) : "—";

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="header-inner">
          <a className="brand" href="/" aria-label="xiaohong.trade 首页"><span>xiaohong</span><b>.trade</b><small>Leverage Radar</small></a>
          <ViewSwitcher value={view} onChange={setView} />
          <div className="header-status"><span>{productionData ? "LIVE DATA" : "DEV FIXTURE"}</span><small>更新 {updated}</small></div>
        </div>
      </header>
      <main className={view === "brief" ? "is-brief" : ""}>
        {!productionData ? <div className="data-banner">当前为明确标记的开发快照；生产环境不会自动回退到 Mock 数据。</div> : null}
        {view !== "brief" ? (
          <>
            <div className="mobile-filter-bar">
              <button type="button" onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen}>筛选 <span>{market === "ALL" ? "全部市场" : MARKET_LABELS[market]} · {category === "ALL" ? "全部分类" : CATEGORY_LABELS[category]}</span></button>
            </div>
            <div className={filtersOpen ? "filters-wrap is-open" : "filters-wrap"}>
              <SignalFilters market={market} category={category} onMarket={setMarket} onCategory={setCategory} />
            </div>
          </>
        ) : null}
        {loading ? <div className="page-state">正在读取信号…</div> : null}
        {error ? <div className="page-state is-error">{error}</div> : null}
        {!loading && !error ? (
          <>
            {view === "brief" ? <MorningBrief signals={signals} allSignals={signals} generatedAt={generatedAt} /> : null}
            {view === "feed" ? <SignalFeed signals={filtered} /> : null}
            {view === "matrix" ? <SignalMatrix signals={filtered} /> : null}
          </>
        ) : null}
      </main>
      <footer>
        <span>xiaohong.trade</span>
        <nav className="footer-tools" aria-label="站内工具">
          <a className="footer-tool" href="/ai-personality/">AI 协作人格测试</a>
          <a className="footer-tool" href="/memo/">共享备忘录</a>
        </nav>
        <p>市场信号研究工具 · 数据变化描述不构成投资建议</p>
      </footer>
    </div>
  );
}
